/**
 * useQuickTasks — Provides quick-add task functionality from the dashboard.
 */
import { useLocalStorage } from './useLocalStorage';
import { useScopedStorageKey } from './useScopedStorageKey';
import { QuickTask, QuickTasksState } from '@/types/quickTask';
import { useMemo, useCallback } from 'react';

const GRACE_PERIOD_DAYS = 7;

const getToday = () => new Date().toISOString().split('T')[0];

const getDaysAgo = (dateStr: string): number => {
  const today = new Date(getToday());
  const taskDate = new Date(dateStr.split('T')[0]);
  const diffMs = today.getTime() - taskDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const isWithinDays = (dateStr: string, days: number): boolean => {
  return getDaysAgo(dateStr) <= days;
};

const formatDaysAgo = (dateStr: string): string => {
  const days = getDaysAgo(dateStr);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

const initialState: QuickTasksState = {
  tasks: [],
  archivedTasks: [],
  lastReviewDate: null,
  hasSeenFeature: false,
};

export function useQuickTasks() {
  // Per-identity bucket: guests never see a signed-in user's to-do list.
  const storageKey = useScopedStorageKey('quick_tasks');
  const { value: state, setValue: setState } = useLocalStorage<QuickTasksState>(storageKey, initialState);

  const today = getToday();

  // Migrate: ensure archivedTasks exists
  const safeState = useMemo(() => ({
    ...state,
    archivedTasks: state.archivedTasks || [],
  }), [state]);

  // Auto-archive tasks older than grace period
  const autoArchive = useCallback(() => {
    const hasOldTasks = safeState.tasks.some(
      task => !isWithinDays(task.createdAt, GRACE_PERIOD_DAYS) && !task.completed
    );
    if (hasOldTasks) {
      setState(prev => {
        const oldTasks = prev.tasks.filter(
          t => !isWithinDays(t.createdAt, GRACE_PERIOD_DAYS) && !t.completed
        );
        const remainingTasks = prev.tasks.filter(
          t => isWithinDays(t.createdAt, GRACE_PERIOD_DAYS) || t.completed
        );
        return {
          ...prev,
          tasks: remainingTasks,
          archivedTasks: [...(prev.archivedTasks || []), ...oldTasks],
        };
      });
    }
  }, [safeState.tasks, setState]);

  // Run auto-archive on mount/change
  useMemo(() => { autoArchive(); }, [autoArchive]);

  // Get today's tasks (created today or carried forward)
  const todaysTasks = useMemo(() => {
    return safeState.tasks.filter(task => {
      const taskDate = task.createdAt.split('T')[0];
      return taskDate === today || task.carriedForward;
    });
  }, [safeState.tasks, today]);

  // Get ALL pending tasks from the last 7 days (not just yesterday)
  const pendingTasks = useMemo(() => {
    return safeState.tasks.filter(task => {
      const taskDate = task.createdAt.split('T')[0];
      const daysOld = getDaysAgo(task.createdAt);
      return daysOld >= 1 && daysOld <= GRACE_PERIOD_DAYS && !task.completed && !task.carriedForward;
    });
  }, [safeState.tasks]);

  // Check if morning review is needed (any pending tasks from past days)
  const needsMorningReview = useMemo(() => {
    return safeState.lastReviewDate !== today && pendingTasks.length > 0;
  }, [safeState.lastReviewDate, today, pendingTasks.length]);

  const addTask = useCallback((text: string) => {
    if (!text.trim()) return;
    
    const newTask: QuickTask = {
      id: crypto.randomUUID(),
      text: text.trim(),
      completed: false,
      createdAt: new Date().toISOString(),
      carriedForward: false,
    };

    setState(prev => ({
      ...prev,
      tasks: [newTask, ...prev.tasks],
    }));
  }, [setState]);

  const toggleTask = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === id ? { ...task, completed: !task.completed } : task
      ),
    }));
  }, [setState]);

  const deleteTask = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.filter(task => task.id !== id),
    }));
  }, [setState]);

  const updateTask = useCallback((id: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === id ? { ...task, text: trimmed } : task
      ),
    }));
  }, [setState]);

  const carryForward = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === id
          ? { ...task, carriedForward: true, createdAt: new Date().toISOString() }
          : task
      ),
    }));
  }, [setState]);

  const clearCompleted = useCallback(() => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.filter(task => !task.completed),
    }));
  }, [setState]);

  const markReviewComplete = useCallback(() => {
    setState(prev => ({
      ...prev,
      lastReviewDate: today,
    }));
  }, [setState, today]);

  const markFeatureSeen = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasSeenFeature: true,
    }));
  }, [setState]);

  const getTask = useCallback((id: string) => {
    return safeState.tasks.find(task => task.id === id);
  }, [safeState.tasks]);

  const restoreArchivedTask = useCallback((id: string) => {
    setState(prev => {
      const task = (prev.archivedTasks || []).find(t => t.id === id);
      if (!task) return prev;
      return {
        ...prev,
        tasks: [{ ...task, createdAt: new Date().toISOString(), carriedForward: true }, ...prev.tasks],
        archivedTasks: (prev.archivedTasks || []).filter(t => t.id !== id),
      };
    });
  }, [setState]);

  const completedCount = useMemo(() => {
    return todaysTasks.filter(t => t.completed).length;
  }, [todaysTasks]);

  return {
    tasks: todaysTasks,
    pendingTasks,
    needsMorningReview,
    completedCount,
    archivedTasks: safeState.archivedTasks,
    hasSeenFeature: safeState.hasSeenFeature,
    addTask,
    toggleTask,
    deleteTask,
    updateTask,
    carryForward,
    clearCompleted,
    markReviewComplete,
    markFeatureSeen,
    getTask,
    restoreArchivedTask,
    getDaysAgoLabel: formatDaysAgo,
  };
}
